import React, { useEffect, useState } from "react";
import {
  Card,
  Select,
  Button,
  Radio,
  Typography,
  message,
  Collapse,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DownOutlined,
} from "@ant-design/icons";
import {
  capitalizeWords,
  filterByNonGpProductId,
} from "../Common/CommonFunction";
import "./BookingForm.css";
import { pushTraceLogEvent, pushTraceLogOnClose, logInteraction } from "./sessionEventPush";
const { Title, Text } = Typography;
const { Option } = Select;

let tempData = [
  {
    value: "15 minutes",
    text: "RECOMMENDED same day appointments for discussing your single medical concern with our expert GPs.",
  },
  {
    value: "30 minutes",
    text: "IDEAL for discussing more than one medical concern, Mental Health Concerns and/or completing bloods tests.",
  },
  {
    value: "45 minutes",
    text: "SUITABLE for discussing multiple medical concerns, and/or conducting multiple tests like blood tests and ECGs.",
  },
  {
    value: "60 minutes",
    text: "PERFECT for discussing complex or multiple medical concerns, and for second opinions with our expert GPs.",
  },
];

const BookingForm = (props) => {
  // console.log(':props BookingForm:::', props)
  // ── Derive category type once ─────────────────────────────────────
  // Booking-journey category always carries `.type` (set by callbackAppointment).
  // Falls back to ID-derived string for backwards-compat (reschedule flow).
  const catType = (
    props?.showAppoinmentData?.clinic?.productCategory?.type ||
    (() => {
      const pid = props?.showAppoinmentData?.clinic?.productCategory?.product_category_id;
      if (pid == 1) return 'gp';
      if (pid == 3) return 'corporate';
      return 'd2c'; // id==2 or any other non-GP/non-corporate
    })()
  );

  // IOO-333: For GP new-booking flow, default to the 15-min duration.
  // Reschedule flow always provides an explicit duration via prefFormData so ?? null still applies.
  const _defaultDurationId = (() => {
    if (props.readOnlyDuration) return null; // reschedule — do not override
    if (catType !== 'gp') return null; // non-GP — no duration picker
    const durationMap = props?.data?.durationObjectById;
    if (durationMap) {
      const entry = Object.values(durationMap).find((d) => Number(d.duration) === 15);
      if (entry) return entry.duration_id;
    }
    return 1; // safe fallback: duration_id 1 is the 15-min product
  })();

  const [selectedDuration, setSelectedDuration] = useState({
    duration: props?.showAppoinmentData?.duration ?? _defaultDurationId ?? null,
    gender: props?.showAppoinmentData?.gender ?? "no_preference",
    // Normalize to String — antd Select uses strict equality for value matching.
    // Handle legacy object {label,value}, plain number, or plain string from state.
    language: String(
      props?.showAppoinmentData?.language?.value
      ?? props?.showAppoinmentData?.language
      ?? "1"
    ),
  });
  const [isHovered, setIsHovered] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [comments, setComments] = useState("");
  const [additionalPrefsOpen, setAdditionalPrefsOpen] = useState(!!props.readOnlyDuration);
  const appointmentForm = (type) => {
    // console.log('SelectedDuration:::', selectedDuration)
    // return
    if (catType === 'gp') {
      if (
        selectedDuration &&
        selectedDuration.duration &&
        selectedDuration.language &&
        selectedDuration.gender
      ) {
        let passPayload = { ...selectedDuration, comments };
        passPayload.type = type;
        const selectedPreferrence = type === "PreferredTime" ? "Preferred Time" : "Preferred Clinic"
        // return
        props.callbackBookingForm(passPayload, type);
        // NOTE: previously fired a dynamically-named event (`book at ${selectedPreferrence} selected`)
        // here while the non-GP branch below fired "preference_selected" for the same action —
        // unified to the same event name for both branches; destination is now a payload field.
        pushTraceLogEvent({
          event: "preference_selected",
          payload: {
            user_id: "",
            date: new Date().toISOString().split("T")[0],
            appointment_duration: selectedDuration.duration,
            preferred_language: selectedDuration.language,
            preferred_gender: selectedDuration.gender,
            destination: selectedPreferrence,
            category_type: "gp",
          },
          step_status: "success",
        });
      } else {
        const validation_error = !selectedDuration.language
          ? "language_missing"
          : !selectedDuration.gender
            ? "gender_missing"
            : "duration_missing";
        pushTraceLogEvent({
          event: "preference_selection_failed",
          payload: { validation_error, category_type: "gp" },
          step_status: "failed",
        });
        if (!selectedDuration.language) {
          messageApi.error({
            type: "error",
            content: "Language selection requied.",
          });
        } else if (!selectedDuration.gender) {
          messageApi.error({
            type: "error",
            content: "Gender selection required.",
          });
        } else if (!selectedDuration.duration) {
          messageApi.error({
            type: "error",
            content: "Appointment duration is required.",
          });
        }
      }
    } else {
      // Non-GP (Corporate, D2C, Health, Dental, etc.)
      if (
        selectedDuration &&
        selectedDuration.language &&
        selectedDuration.gender
      ) {
        let passPayload = {
          ...selectedDuration,
          comments,
          type,
          non_gp_product_id: props?.showAppoinmentData?.clinic?.non_gp_product_id ?? null,
        };
        // return
        props.callbackBookingForm(passPayload, type);
        pushTraceLogEvent({
          event: "preference_selected",
          payload: {
            user_id: "",
            date: new Date().toISOString().split("T")[0],
            appointment_duration: selectedDuration.duration,
            preferred_language: selectedDuration.language,
            preferred_gender: selectedDuration.gender,
            destination: type === "PreferredTime" ? "Preferred Time" : "Preferred Clinic",
            category_type: "non_gp",
          },
          step_status: "success",
        });
      } else {
        pushTraceLogEvent({
          event: "preference_selection_failed",
          payload: {
            validation_error: !selectedDuration.language ? "language_missing" : "gender_missing",
            category_type: "non_gp",
          },
          step_status: "failed",
        });
        if (!selectedDuration.language) {
          messageApi.error({
            type: "error",
            content: "Language selection required.",
          });
        } else if (!selectedDuration.gender) {
          messageApi.error({
            type: "error",
            content: "Gender selection required.",
          });
        }
      }
    }
  };
  const stripHtmlTags = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      // console.log("visibility", document.visibilityState);

      if (document.visibilityState === "hidden") {
        pushTraceLogOnClose({
          event: "browser_closed_or_minimized",
          payload: {
            step: "appointment_duration_selection",
          },
          step_status: "success",
          meta: {
            reason: "tab_close_or_background",
          },
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [document.visibilityState]);

  return (
    <div style={{ maxWidth: 620, margin: "auto", padding: 20 }}>
      {contextHolder}
      <div
        style={{
          maxWidth: 605,
          background: "#00838540",
          padding: 10,
          marginBottom: 20,
        }}
      >
        <Title
          style={{
            margin: 0,
            fontSize: 20,
            color: "#008385",
            fontWeight: "400",
          }}
        >
          {/* {capitalizeWords('My booking')}: */}
          My booking:
        </Title>
      </div>

      <div
        className="summuryCard"
        style={{ borderColor: "#008385", maxWidth: 393, minHeight: 53, height: 'auto', padding: '5px 0' }}
      >
        {props?.showAppoinmentData?.clinic?.productCategory?.name || ""}
        {props?.showAppoinmentData?.clinic?.selectedCorporate && props?.showAppoinmentData?.clinic?.selectedCorporate?.name && !props?.showAppoinmentData?.clinic?.selectedCorporate?.change_category_label && (
          <>
            <br />
            {props?.showAppoinmentData?.clinic?.selectedCorporate?.name}
          </>
        )}
        {props?.showAppoinmentData?.clinic?.productData?.name && (
          <>
            <br />
            {props?.showAppoinmentData?.clinic?.productData?.name}
          </>
        )}
      </div>
      {/* Duration picker — GP only, hidden during reschedule */}
      {catType === 'gp' && !props.readOnlyDuration && (
        <>
          {(
            <>
              <div
                style={{
                  maxWidth: 605,
                  background: "#00838540",
                  padding: 10,
                  marginTop: 40,
                }}
              >
                <Title
                  style={{
                    margin: 0,
                    fontSize: 20,
                    color: "#008385",
                    fontWeight: "400",
                  }}
                >
                  {/* {capitalizeWords('Appointment duration')} */}
                  Appointment duration:
                </Title>
              </div>
              <div
                style={{
                  background: "rgba(240, 250, 250, 1)",
                  paddingBlock: 30,
                }}
              >
                <Radio.Group
                  value={selectedDuration.duration}
                  onChange={(e) => {
                    logInteraction("duration_selected", { duration_id: e.target.value });
                    setSelectedDuration((previousData) => ({
                      ...previousData,
                      duration: e.target.value,
                    }));
                  }}
                  disabled={!!props.readOnlyDuration}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    {/* { && props tempData.map((item) => ( */}
                    {props &&
                      props.data &&
                      props.data.durationObjectById &&
                      Object.values(props.data.durationObjectById).map(
                        (item) => (
                          <div
                            key={item.duration_id}
                            style={{
                              width: 350,
                              margin: "auto",
                              textAlign: "left",
                            }}
                          >
                            <Radio
                              className="round-checkbox "
                              value={item.duration_id}
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <>{item.duration} minutes</>
                            </Radio>
                            <p
                              style={{
                                margin: 0,
                                paddingLeft: 28,
                                fontSize: 12,
                                fontWeight: "400",
                                color: "#4A4A4A",
                              }}
                            >
                              {stripHtmlTags(item.content)}
                            </p>
                          </div>
                        ),
                      )}
                  </div>
                </Radio.Group>
                {/* IOO-198: Shown only during reschedule — duration locked */}
                {props.readOnlyDuration && (
                  <p style={{ textAlign: 'center', color: '#888', fontSize: 12, marginTop: 6 }}>
                    Duration cannot be changed during rescheduling.
                  </p>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Additional preferences — GP always, Non-GP only if at least one allow flag is true (reschedule included) */}
      {(() => {
        const _allowLang =
          catType === 'gp' ||
          props?.showAppoinmentData?.selectedProduct?.allow_language_selection ||
          props?.showAppoinmentData?.data?.product_data?.allow_language_selection ||
          props?.showAppoinmentData?.product_data?.allow_language_selection ||
          props?.showAppoinmentData?.clinic?.productData?.allow_language_selection ||
          props?.showAppoinmentData?.clinic?.productCategory?.allow_language_selection;
        const _allowGender =
          catType === 'gp' ||
          props?.showAppoinmentData?.selectedProduct?.allow_gender_selection ||
          props?.showAppoinmentData?.data?.product_data?.allow_gender_selection ||
          props?.showAppoinmentData?.product_data?.allow_gender_selection ||
          props?.showAppoinmentData?.clinic?.productData?.allow_gender_selection ||
          props?.showAppoinmentData?.clinic?.productCategory?.allow_gender_selection;
        if (!_allowLang && !_allowGender) return null;
        return (
        <div style={{ maxWidth: 605, marginTop: 40 }}>
          <Collapse
            className="collapseadditional"
            activeKey={additionalPrefsOpen ? ["1"] : []}
            onChange={(key) => {
              logInteraction("additional_preferences_toggled", { open: key.length > 0 });
              setAdditionalPrefsOpen(key.length > 0);
            }}
            expandIcon={({ isActive }) => (
              <DownOutlined
                rotate={isActive ? 180 : 0}
                style={{ color: "#008385" }}
              />
            )}
            expandIconPosition="end"
            style={{
              border: "none",
              borderRadius: 0,
              backgroundColor: "transparent",
            }}
            items={[
              {
                key: "1",
                label: (
                  <Title
                    style={{
                      margin: 0,
                      fontSize: 20,
                      color: "#008385",
                      fontWeight: "400",
                    }}
                  >
                    Additional preferences:
                  </Title>
                ),
                style: {
                  backgroundColor: "#00838540",
                  borderRadius: 0,
                  border: "none",
                },
                children: (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "20px",
                        alignItems: "start",
                      }}
                      className="preferSelection"
                    >
                      {/* Language picker — GP always; Non-GP when allow_language_selection is true */}
                      {(catType === 'gp' ||
                        props?.showAppoinmentData?.selectedProduct?.allow_language_selection ||
                        props?.showAppoinmentData?.data?.product_data?.allow_language_selection ||
                        props?.showAppoinmentData?.product_data?.allow_language_selection ||
                        props?.showAppoinmentData?.clinic?.productData?.allow_language_selection ||
                        props?.showAppoinmentData?.clinic?.productCategory?.allow_language_selection) && (
                        <div
                          style={{ backgroundColor: "#F0FAFA", width: "280px" }}
                        >
                          <div
                            style={{ marginBottom: "10px", padding: "10px" }}
                          >
                            <Text
                              style={{
                                fontSize: 16,
                                color: "#008385",
                                fontWeight: "500",
                              }}
                            >
                              Preferred language:
                            </Text>
                          </div>
                          <Select
                            value={String(selectedDuration.language)}
                            onChange={(lan) => {
                              logInteraction("language_selected", { language: lan });
                              setSelectedDuration((previousData) => ({
                                ...previousData,
                                language: String(lan),
                              }));
                            }}
                            placeholder="Select language"
                            style={{ width: "100%" }}
                          >
                            {props &&
                              props.data &&
                              props.data.languageData &&
                              props.data.languageData.map((item) => (
                                <Option key={String(item.value)} value={String(item.value)}>
                                  {item.label}
                                </Option>
                              ))}
                          </Select>
                        </div>
                      )}
                      {/* Gender picker — GP always; Non-GP when allow_gender_selection is true */}
                      {(catType === 'gp' ||
                        props?.showAppoinmentData?.selectedProduct?.allow_gender_selection ||
                        props?.showAppoinmentData?.data?.product_data?.allow_gender_selection ||
                        props?.showAppoinmentData?.product_data?.allow_gender_selection ||
                        props?.showAppoinmentData?.clinic?.productData?.allow_gender_selection ||
                        props?.showAppoinmentData?.clinic?.productCategory?.allow_gender_selection) && (
                        <div
                          style={{ backgroundColor: "#F0FAFA", width: "280px" }}
                        >
                          <div
                            style={{ marginBottom: "10px", padding: "10px" }}
                          >
                            <Text
                              style={{
                                fontSize: 16,
                                color: "#008385",
                                fontWeight: "500",
                              }}
                            >
                              Preferred gender:
                            </Text>
                          </div>
                          <Select
                            value={selectedDuration.gender}
                            onChange={(gen) => {
                              logInteraction("gender_selected", { gender: gen });
                              setSelectedDuration((previousData) => ({
                                ...previousData,
                                gender: gen,
                              }));
                            }}
                            placeholder="Select Gender"
                            style={{ width: "100%" }}
                          >
                            <Option value="no_preference">No preference</Option>
                            <Option value="male">Male</Option>
                            <Option value="female">Female</Option>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </div>
      );
      })()}

      <div
        style={{
          maxWidth: 605,
          background: "#00838540",
          padding: 10,
          marginTop: 40,
        }}
      >
        <Title
          style={{
            margin: 0,
            fontSize: 20,
            color: "#008385",
            fontWeight: "400",
          }}
        >
          {/* {capitalizeWords('Booking preference')}: */}
          Booking preference:
        </Title>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 10,
          marginTop: "20px",
          position: "relative",
        }}
      >
        <Button
          shape="circle"
          icon={
            <ArrowLeftOutlined style={{ color: "#008385", fontSize: "18px" }} />
          }
          style={{
            backgroundColor: "#C8E6E6", // Light teal
            border: "none",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: 10,
            marginRight: 10,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            position: "absolute",
            left: 0,
          }}
          onClick={() => {
            props.callBackForbackpage("APPOINMENT");
            pushTraceLogEvent({
              event: "navigate_to_appointment_type_selection_page",
              payload: {
                action: "navigate to appointment type selection page",
              },
              step_status: "success",
            });
          }}
        />
        {!props.readOnlyDuration && (
          <Button
            // type="primary"
            className="transparentBtn"
            style={{ borderColor: "#008385", padding: "0 22px", height: 60 }}
            // onMouseEnter={() => setIsHovered(1)}
            // onMouseLeave={() => setIsHovered(0)}
            onClick={() => appointmentForm("PreferredTimeclinic")}
          >
            {/* {capitalizeWords('Book at preferred clinic')} */}
            Book at preferred clinic
          </Button>
        )}
        <Button
          // type="primary"
          // className="darkBtn"
          className="transparentBtn"
          style={{ borderColor: "#008385", padding: "0 22px", height: 60 }}
          // onMouseEnter={() => setIsHovered(2)}
          // onMouseLeave={() => setIsHovered(0)}
          onClick={() => appointmentForm("PreferredTime")}
        >
          {/* {capitalizeWords('Book at preferred time')} */}
          Book at preferred time
        </Button>
      </div>
    </div>
  );
};

export default BookingForm;
